import { lazy, Suspense } from 'react'
import { createBrowserRouter } from 'react-router-dom'
import { RootLayout } from '@/components/layouts/RootLayout'
import { MainLayout } from '@/components/layouts/MainLayout'
import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'

// Lazy load pages for code splitting
const HomePage = lazy(() => import('@/pages/HomePage').then(m => ({ default: m.HomePage })))
const NotFound = lazy(() => import('@/pages/NotFound').then(m => ({ default: m.NotFound })))

// Teams
const TeamsListPage = lazy(() => import('@/pages/teams/TeamsListPage').then(m => ({ default: m.TeamsListPage })))
const TeamDetailPage = lazy(() => import('@/pages/teams/TeamDetailPage').then(m => ({ default: m.TeamDetailPage })))

// Players
const PlayersListPage = lazy(() => import('@/pages/players/PlayersListPage').then(m => ({ default: m.PlayersListPage })))
const PlayerDetailPage = lazy(() => import('@/pages/players/PlayerDetailPage').then(m => ({ default: m.PlayerDetailPage })))

// News
const NewsListPage = lazy(() => import('@/pages/news/NewsListPage').then(m => ({ default: m.NewsListPage })))
const NewsDetailPage = lazy(() => import('@/pages/news/NewsDetailPage').then(m => ({ default: m.NewsDetailPage })))

// Points Table
const PointsTablePage = lazy(() => import('@/pages/PointsTablePage').then(m => ({ default: m.PointsTablePage })))

// Matches (keeping existing for now)
const MatchesPage = lazy(() => import('@/pages/MatchesPage').then(m => ({ default: m.MatchesPage })))

const GalleryListPage = lazy(() =>
  import('@/pages/media/GalleryListPage').then(m => ({ default: m.GalleryListPage }))
)
const GalleryDetailPage = lazy(() =>
  import('@/pages/media/GalleryDetailPage').then(m => ({ default: m.GalleryDetailPage }))
)
const YoutubeVideosPage = lazy(() =>
  import('@/pages/media/YoutubeVideosPage').then(m => ({ default: m.YoutubeVideosPage }))
)

// Wrapper for lazy loaded components
function LazyPage({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen />}>
      {children}
    </Suspense>
  )
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        element: <MainLayout />,
        children: [
          // Home
          {
            index: true,
            element: <LazyPage><HomePage /></LazyPage>,
          },

          // News Routes
          {
            path: 'news',
            children: [
              {
                index: true,
                element: <LazyPage><NewsListPage /></LazyPage>,
              },
              {
                path: ':slug',
                element: <LazyPage><NewsDetailPage /></LazyPage>,
              },
            ],
          },

          // Teams Routes
          {
            path: 'teams',
            children: [
              {
                index: true,
                element: <LazyPage><TeamsListPage /></LazyPage>,
              },
              {
                path: ':id',
                element: <LazyPage><TeamDetailPage /></LazyPage>,
              },
            ],
          },

          // Players Routes
          {
            path: 'players',
            children: [
              {
                index: true,
                element: <LazyPage><PlayersListPage /></LazyPage>,
              },
              {
                path: ':id',
                element: <LazyPage><PlayerDetailPage /></LazyPage>,
              },
            ],
          },

          // Matches
          {
            path: 'matches',
            element: <LazyPage><MatchesPage /></LazyPage>,
          },

          // Points Table
          {
            path: 'points-table',
            element: <LazyPage><PointsTablePage /></LazyPage>,
          },

          // Media Routes
          {
            path: 'media',
            children: [
              {
                path: 'videos',
                element: <LazyPage><YoutubeVideosPage /></LazyPage>,
              },
              {
                path: 'gallery',
                element: <LazyPage><GalleryListPage /></LazyPage>,
              },
              {
                path: 'gallery/:id',
                element: <LazyPage><GalleryDetailPage /></LazyPage>,
              },
            ],
          },
        ],
      },

      // 404
      {
        path: '*',
        element: <LazyPage><NotFound /></LazyPage>,
      },
    ],
  },
])
